import { SimpleSearchFilter } from 'api/dashboard/widget';
import { EmailUser } from 'api/pushes/models';
import { IntegrationConfig } from '../types';

export type SalesforceIntegrationId = { integrationId: string | 'new' };

export type SalesforceIntegrationProvider = {
  authType: string;
  oauthUrl: string;
  oauthSandboxUrl: string;
  oauthReauthUrl: string;
  marketplaceAppUrl: string;
  marketplaceAppInstallationRequired?: boolean;
};

export enum SalesforceActions {
  Project = 'project',
  Task = 'task',
}

export enum SalesforceActionsV2 {
  Project = 'scarletProject',
  Task = 'task',
  Account = 'company',
  Contact = 'user',
}

export type SalesforceObject = {
  id: string;
  name: string;
  url?: string;
  type?: string;
};

export enum AutomationOperation {
  Upsert = 'UPSERT',
  UpsertRelatedRecords = 'UPSERT_RELATED_RECORDS',
}

export type Condition = {
  name: string;
  value: string;
  operation: string;
};

export enum IntegrationSource {
  NativeIntegration = 'NATIVE_INTEGRATION',
}

export type SalesforceAutomationCondition = Omit<
  SimpleSearchFilter,
  'customFields'
>;

export type SalesforceAutomationAction = {
  integrationType: string;
  resourceType: SalesforceActions | SalesforceActionsV2;
  operation: AutomationOperation;
  inputs: Record<string, unknown>;
  dependentActions?: SalesforceAutomationAction[];
  sourceParentResourceType?: string;
  generateAutomationForFutureInserts?: boolean;
  sourceParentResourceId?: string;
  sourceParentResourceFieldId?: string;
  sourceResourceType?: string;
  sourceRelationshipName?: string;
};

export type SalesforceOrderPayload = {
  automationId: number;
  automationUpdatedOrder: number;
};

export type SalesforceAutomation = {
  actions: SalesforceAutomationAction[];
  automationId?: number;
  automationName?: string;
  description?: string;
  createdAt: string;
  order: number;
  integrationType: string;
  enabled: boolean;
  matchingCondition?: SalesforceAutomationCondition;
  object?: string;
  triggerCondition: SalesforceAutomationCondition;
  source: IntegrationSource;
  sourceId: number;
  updatedAt: string;
  automationType?: 'CRM' | 'CRM_V2';
};

export type CreateAutomationPayload = Omit<
  SalesforceAutomation,
  'automationId' | 'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type UpdateAutomationPayload = Omit<
  SalesforceAutomation,
  'createdAt' | 'deleted' | 'order' | 'updatedAt'
>;

export type SalesforceSettingsMeta = {
  externalAccountId: string;
  errorEmailIntervalSeconds?: number;
  errorEmailEnabled?: boolean;
  errorEmailRecipients?: EmailUser[];
};

export type SalesforceConfig = IntegrationConfig<SalesforceSettingsMeta>;

export enum SalesforceSyncTypes {
  SyncTwoWay = 'SYNC_TWO_WAY',
  SyncNativeToExternal = 'SYNC_NATIVE_TO_EXTERNAL',
  SyncExternalToNative = 'SYNC_EXTERNAL_TO_NATIVE',
  None = 'NONE',
}

export type SalesforceFieldMapping = {
  fieldMappingConfigId?: number;
  integrationId: number;
  externalIntegrationType: 'salesforce'; // consciously hardcoded. Providing options doesn't make sense for a fixed value
  resouceMappingConfigId: number;
  nativeFieldId: number;
  nativeFieldName?: string;
  nativeFieldLabel: string;
  externalFieldId: string;
  externalFieldName: string;
  syncType: SalesforceSyncTypes;
  enabled?: boolean;
  allowedSyncTypes: [SalesforceSyncTypes];
};

export type SalesforceResourceMapping = {
  resourceMappingConfigId: number;
  integrationId: number;
  externalIntegrationType: 'salesforce'; // consciously hardcoded. Providing options doesn't make sense for a fixed value
  nativeResourceType: string;
  externalResourceType: string;
  syncType: SalesforceSyncTypes;
  fieldMappings: SalesforceFieldMapping[];
  enabled?: boolean;
};

export type SalesforceResourceCreatePayload = Pick<
  SalesforceResourceMapping,
  | 'externalIntegrationType'
  | 'externalResourceType'
  | 'nativeResourceType'
  | 'syncType'
  | 'enabled'
>;

// TODO: @krishna Remove this once the error codes are standardised
export enum AutomationErrorCodes {
  ConflictingFieldValue = 'CONFLICTING_FIELD_VALUE',
}

export type AutomationError = {
  code: AutomationErrorCodes;
  reason: string;
};

export interface SfInstallations {
  installationId: number;
  externalInstallationId: string;
  externalIntegrationType: string;
  externalAccountId: string;
  installedAppId: string;
  installedAppName: string;
  installedAppVersionId: string;
  installedAppVersionName: string;
  // TODO: change this to enum
  status: string;
}

export type ChildResource = {
  sourceResourceType: string;
  sourceRelationshipName: string;
  sourceParentResourceFieldId?: string;
};

export type ChildResourcesResponse = {
  sourceParentResourceId: string;
  sourceParentResourceType: string;
  salesforceRelatedResources: ChildResource[];
};

export type ContactFieldMappingDetail = {
  fieldMappingConfigId: number;
  integrationId: number;
  externalIntegrationType: string;
  nativeResourceType: string;
  externalResourceType: string;
  resouceMappingConfigId: number;
  nativeFieldName: string;
  nativeFieldLabel: string;
  externalFieldName: string | null;
  externalFieldLabel: string | null;
  externalFieldId: string | null;
  syncType: string;
  allowedSyncTypes?: string[];
  fieldType?: string;
  fieldDataType?: string;
  enabled: boolean;
  deleted: boolean;
};

export type ContactFieldSyncDetailConfig = {
  resourceMappingConfigId: number;
  integrationId: number;
  externalIntegrationType: string;
  nativeResourceType: string;
  externalResourceType: string;
  syncType: string;
  fieldMappings?: ContactFieldMappingDetail[];
  enabled: boolean;
  deleted: boolean;
};

export type MappableSalesforceField = {
  integrationFieldId: string;
  fieldName: string;
  fieldLabel: string;
  fieldDataType?: string;
  fieldType?: string;
  isEnabled?: boolean;
  required?: boolean;
  editableByFieldMapping?: boolean;
  parentFieldName?: string;
  externalChildResourceType?: string;
  externalChildFieldId?: string;
  externalParentFieldId?: string;
};

export type MappableSalesforceFieldsResponse = MappableSalesforceField[];

export type GetMappableSalesforceFieldsParams = {
  integrationId: number;
  externalResourceType: string;
  nativeResourceType: string;
  nativeFieldName: string;
};

export type SaveContactFieldMappingPayload = {
  fieldMappingConfigId?: number | null;
  integrationId: number;
  externalIntegrationType: string;
  resouceMappingConfigId?: number | null;
  nativeFieldName: string;
  nativeFieldLabel: string;
  externalFieldId: string;
  externalFieldName: string;
  syncType: string;
  allowedSyncTypes?: string[];
  externalChildResourceType?: string;
  externalChildFieldId?: string;
  externalParentFieldId?: string;
  enabled: boolean;
  deleted: boolean;
};

export type SaveContactFieldSyncPayload = {
  resourceMappingConfigId?: number | null;
  integrationId: number;
  externalIntegrationType: string;
  nativeResourceType: string;
  externalResourceType: string;
  syncType: string;
  fieldMappings: SaveContactFieldMappingPayload[];
  enabled: boolean;
  deleted: boolean;
};

export type SaveContactFieldSyncResponse = ContactFieldSyncDetailConfig;
